import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
// import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { formatDateForAPI } from "../Utility/const";
import {
  getrestrictedAreaGssLoadDaily,
  getGssrestrictedAreaLoadMonthly,
  getRestrictedGssLoadDailyDiscom,
  getRestrictedMonthlyDiscomGssLoad,
  getGssLoadRestrictedDistrictDailyDiscom,
  getMonthlyRestrictedDistrictDiscomGssLoad,
} from "../Utility/RestApi";

const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
  {
    label: "District",
    key: "district",
    values: ["district", "discom"],
  },
];

const DISCOM = [
  { label: "Jodhpur", key: "Jodhpur", apiKey: "JDVVNL" },
  { label: "Jaipur", key: "Jaipur", apiKey: "JVVNL" },
  { label: "Ajmer", key: "Ajmer", apiKey: "AVVNL" },
];

// Helper function to convert display names to API keys
function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

// Helper function to convert API keys to display names
function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | number | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) {
    return { month: 0, year: 0 };
  }
  
  // If it's a number (month only), return it directly
  if (typeof monthYearStr === "number") {
    return { month: monthYearStr, year: 0 };
  }
  
  // If it's a string, parse it
  if (typeof monthYearStr === "string") {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = monthYearStr.split("-");
    if (parts.length !== 2) {
      return { month: 0, year: 0 };
    }
    const [monthStr, yearStr] = parts;
    const month = monthNames.indexOf(monthStr) + 1;
    const year = 2000 + parseInt(yearStr);
    return { month, year };
  }
  
  return { month: 0, year: 0 };
}

interface RajasthanDrawalPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
}

export function RajasthanDrawalPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
}: RajasthanDrawalPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) {
        return;
      }
      const dates = DefaultDates[activeTab].defaultRangeDate;

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      let allData: any[] = [];

      // Determine which API to call based on grouping type only
      const isAreaGrouping = selectedGroupKeys[0]?.key === "area";
      const isDiscomGrouping = selectedGroupKeys[0]?.key === "discom";
      const isDistrictGrouping = selectedGroupKeys[0]?.key === "district";

      if (selectedXaxis.key === "month") {
        // Monthly data
        if (isAreaGrouping) {
          // Area grouping - use area-level API to get station-level details
          const monthData = await getGssrestrictedAreaLoadMonthly(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(monthData)) {
            allData = monthData;
          }
        } else if (isDiscomGrouping) {
          // Discom grouping - use discom-level API
          const monthlyDiscomData = await getRestrictedMonthlyDiscomGssLoad(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(monthlyDiscomData)) {
            allData = monthlyDiscomData;
          }
        } else if (isDistrictGrouping) {
          // District grouping - use district-level API
          const monthlyDistrictData = await getMonthlyRestrictedDistrictDiscomGssLoad(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(monthlyDistrictData)) {
            allData = monthlyDistrictData;
          }
        }
      } else {
        // Daily data
        if (isAreaGrouping) {
          // Area grouping - use area-level API to get station-level details
          const dailyData = await getrestrictedAreaGssLoadDaily(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(dailyData)) {
            allData = dailyData;
          }
        } else if (isDiscomGrouping) {
          // Discom grouping - use discom-level API
          const discomData = await getRestrictedGssLoadDailyDiscom(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(discomData)) {
            allData = discomData;
          }
        } else if (isDistrictGrouping) {
          // District grouping - use district-level API
          const districtData = await getGssLoadRestrictedDistrictDailyDiscom(
            startDateStr,
            endDateStr
          );
          if (Array.isArray(districtData)) {
            allData = districtData;
          }
        }
      }

      // Normalize data format for district grouping
      if (isDistrictGrouping && allData.length > 0) {
        allData = allData.map((item) => {
          const normalized: any = { ...item };
          
          // Ensure district field is populated (it should come directly from API as "district")
          // The API response shows district field exists, so just ensure it's not undefined/null
          if (!normalized.district || normalized.district === "undefined" || normalized.district === "null") {
            normalized.district = item.discom; // Fallback to discom if district is missing
          }
          
          // Normalize field names from district API to match expected format
          // District API uses: peak_load_mw, average_load_mw
          // Expected format: daily_peak_load_mw, avg_daily_load_mw
          if (item.peak_load_mw !== undefined && !item.daily_peak_load_mw) {
            normalized.daily_peak_load_mw = item.peak_load_mw;
          }
          if (item.average_load_mw !== undefined && !item.avg_daily_load_mw) {
            normalized.avg_daily_load_mw = item.average_load_mw;
          }
          
          // For monthly data with numeric month format, convert to "Jan-24" format
          if (selectedXaxis.key === "month" && typeof item.month === "number" && item.year) {
            const monthNames = [
              "Jan", "Feb", "Mar", "Apr", "May", "Jun",
              "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
            ];
            const yearShort = item.year.toString().slice(-2);
            normalized.month = `${monthNames[item.month - 1]}-${yearShort}`;
            normalized.monthNumber = item.month; // Keep original for reference
            normalized.year = item.year;
          }
          
          return normalized;
        });
      }

      setData(allData);

      // Set unique dates for date filter
      if (selectedXaxis.key === "date") {
        setUniqueDate(
          Array.from(
            new Set(
              allData
                .map((item: any) => item.date)
                .filter((date: any) => date !== undefined)
                .sort(
                  (a: string, b: string) =>
                    new Date(a).getTime() - new Date(b).getTime()
                )
            )
          )
        );
      } else {
        // For monthly data, create unique month options
        const uniqueMonths = Array.from(
          new Set(
            allData
              .map((item: any) => item.month)
              .filter((month: any) => month !== undefined)
          )
        ).sort((a: string, b: string) => {
          const aMonthYear = parseMonthYear(a);
          const bMonthYear = parseMonthYear(b);
          if (aMonthYear.year !== bMonthYear.year) {
            return aMonthYear.year - bMonthYear.year;
          }
          return aMonthYear.month - bMonthYear.month;
        });
        setUniqueDate(uniqueMonths);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key, selectedGroupKeys]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    // Filter by discom - convert display names to API keys for filtering
    if (selectedDiscom.length > 0) {
      const apiDiscomKeys = getApiDiscomKeys(selectedDiscom);
      filteredData = filteredData.filter((item) =>
        apiDiscomKeys.includes(item.discom)
      );
    }

    // Filter by date or month
    if (selectedDate.length > 0) {
      if (selectedXaxis.key === "date") {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.date)
        );
      } else {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.month)
        );
      }
    }

    // Helper to get search label based on grouping
    const getSearchLabel = (item: any) => {
      const discomName = getDisplayDiscomName(item.discom);
      if (!discomName || discomName === "undefined" || discomName === "null") return "";

      if (selectedGroupKeys[0]?.key === "area") {
        const name = item.area || item.station;
        if (!name || name === "undefined" || name === "null" || name === "UNKNOWN") return "";
        return `${name} (${discomName})`;
      } else if (selectedGroupKeys[0]?.key === "district") {
        const name = item.district || item.divisional_headquarter;
        if (!name || name === "undefined" || name === "null" || name === "UNKNOWN") return "";
        return `${name} (${discomName})`;
      } else if (selectedGroupKeys[0]?.key === "discom") {
        return discomName;
      }
      return "";
    };

    // Populate Search Area options for ALL groupings
    setUniqueArea(
      Array.from(
        new Set(
          filteredData.map((item) => getSearchLabel(item))
        )
      ).filter(label => label && 
                        !label.includes("undefined") && 
                        !label.includes("null") && 
                        !label.startsWith("UNKNOWN"))
       .sort()
    );

    // Apply Search Area filter
    if (selectedArea.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedArea
          .map((opt) => opt.value)
          .includes(getSearchLabel(item))
      );
    }

    // Sort by date or month
    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        if (!a.month || !b.month) return 0;
        
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        
        const aYear = aMonthYear.year || a.year || 0;
        const bYear = bMonthYear.year || b.year || 0;
        
        if (aYear !== bYear) {
          return aYear - bYear;
        }
        return aMonthYear.month - bMonthYear.month;
      } else {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    // Handle district data - ensure district field has a valid value
    if (selectedGroupKeys[0]?.key === "district") {      
      filteredData = filteredData.map((item) => {
        // Check multiple possible field names for district/div HQ
        let districtValue = item.district || item.divisional_headquarter || item.div_hq;
        
        // If still undefined, null, or empty string, use discom as the district value
        // This way we still show the data grouped by discom when district is not available
        if (!districtValue || 
            districtValue === "undefined" || 
            districtValue === "null" ||
            String(districtValue).trim() === "") {
          districtValue = item.discom; // Use discom when district is not available
        }
        
        return {
          ...item,
          district: districtValue,
        };
      });
    }

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
    const convertedData = filteredData.map((item) => {
      // Handle different data structures based on grouping
      // District API uses: peak_load_mw, morning_peak_load_mw, evening_peak_load_mw, average_load_mw
      // Area/Discom API uses: daily_peak_load_mw, morning_peak_load_mw, evening_peak_load_mw, avg_daily_load_mw
      
      const isDistrictData = selectedGroupKeys[0]?.key === "district";
      
      return {
        ...item,
        daily_peak_load_mw: item.daily_peak_load_mw
          ? item.daily_peak_load_mw * conversionFactor
          : 0,
        morning_peak_load_mw: item.morning_peak_load_mw
          ? item.morning_peak_load_mw * conversionFactor
          : 0,
        evening_peak_load_mw: item.evening_peak_load_mw
          ? item.evening_peak_load_mw * conversionFactor
          : 0,
        avg_daily_load_mw: item.avg_daily_load_mw
          ? item.avg_daily_load_mw * conversionFactor
          : 0,
      };
    });

    let aggregationKeys = [
      "daily_peak_load_mw",
      "morning_peak_load_mw",
      "evening_peak_load_mw",
    ];
    let groupByKeys: string[] = [];

    // Determine groupBy keys based on selected group
    if (selectedGroupKeys[0]?.key === "area") {
      // Always group by area+discom for station-level breakdown
      groupByKeys = ["area", "discom"];
    } else if (selectedGroupKeys[0]?.key === "discom") {
      groupByKeys = ["discom"];
    } else if (selectedGroupKeys[0]?.key === "district") {
      groupByKeys = ["district", "discom"];
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
  ]);

  function getLegends() {
    const peakTypes = [
      { field: "daily_peak_load", displayName: "Day Peak" },
      { field: "morning_peak_load", displayName: "Morning Peak" },
      { field: "evening_peak_load", displayName: "Evening Peak" },
    ];

    const legends: any[] = [];

    chartKeys.forEach((key) => {
      let displayKey = key;

      // Handle different grouping scenarios
      if (selectedGroupKeys[0]?.key === "area") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const stationOrAreaKey = parts.slice(0, -1).join("_");
          
          const discomDisplayName = getDisplayDiscomName(discomKey);
          
          if (stationOrAreaKey.toLowerCase() === "rajasthan") {
            displayKey = discomDisplayName;
          } else {
            displayKey = `${stationOrAreaKey} (${discomDisplayName})`;
          }
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      } else if (selectedGroupKeys[0]?.key === "discom") {
        displayKey = getDisplayDiscomName(key);
      } else if (selectedGroupKeys[0]?.key === "district") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const districtKey = parts.slice(0, -1).join("_");
          
          const discomDisplayName = getDisplayDiscomName(discomKey);
          
          if (!districtKey || districtKey === "undefined" || districtKey.trim() === "") {
            displayKey = discomDisplayName;
          } else {
            displayKey = `${districtKey} (${discomDisplayName})`;
          }
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      }

      peakTypes.forEach((peak) => {
        const fieldSuffix = `${peak.field}_mw`;
        const legendDisplayName = `${displayKey} ${peak.displayName}`;
        legends.push({
          dataKey: `${key}_${fieldSuffix}`,
          name: legendDisplayName,
          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? peak.field : undefined,
        });
      });
    });

    return legends;
  }

  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">
              Restricted Demand Peak Analysis ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              <div className="min-w-60">
                <SearchBox
                  options={uniqueArea.map((item) => ({
                    value: item,
                    label: item,
                  }))}
                  isMany={true}
                  placeholder="Search Area"
                  selectedOptions={selectedArea}
                  onChange={(v: any) => {
                    setSelectedArea(v);
                  }}
                />
              </div>
              {/* <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              /> */}
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(tablePar);
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(tablePar);
                  } else if (dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey={selectedXaxis.values[0]}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand) 
2. MW = Represents the average (MW) when the selected time interval is Date or Month.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}